import { useId, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

import { cn } from '../lib/cn';

export type OtpInputProps = {
  value: string;
  onChange: (value: string) => void;
  /** Appelé dès que les `length` chiffres sont saisis (frappe ou collage). */
  onComplete?: (value: string) => void;
  length?: number;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Id du premier champ, pour l'associer à un `<label htmlFor>`. */
  id?: string;
  'aria-describedby'?: string;
  className?: string;
};

const onlyDigits = (raw: string) => raw.replace(/\D/g, '');

/**
 * Saisie d'un code à usage unique, une case par chiffre.
 * Gère le collage du code entier, les flèches, l'effacement et l'auto-avance.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  invalid,
  disabled,
  autoFocus,
  id,
  className,
  ...props
}: OtpInputProps) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const focusAt = (index: number) => {
    const target = inputs.current[Math.max(0, Math.min(length - 1, index))];
    target?.focus();
    target?.select();
  };

  const commit = (next: string, focusIndex: number) => {
    const trimmed = next.slice(0, length);
    onChange(trimmed);
    focusAt(focusIndex);
    if (trimmed.length === length) onComplete?.(trimmed);
  };

  const handleChange = (index: number, raw: string) => {
    const digits = onlyDigits(raw);
    if (!digits) return;

    // Saisie normale : un chiffre remplace la case courante.
    // Collage dans une case : les chiffres remplissent les cases suivantes.
    const chars = value.padEnd(length, ' ').split('');
    for (let offset = 0; offset < digits.length && index + offset < length; offset += 1) {
      chars[index + offset] = digits.charAt(offset);
    }
    commit(chars.join('').trimEnd(), index + digits.length);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const chars = value.split('');
      if (chars[index]) {
        chars[index] = '';
        commit(chars.join('').trimEnd(), index);
      } else if (index > 0) {
        chars[index - 1] = '';
        commit(chars.join('').trimEnd(), index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAt(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const digits = onlyDigits(event.clipboardData.getData('text'));
    if (!digits) return;
    event.preventDefault();
    commit(digits.slice(0, length), digits.length);
  };

  return (
    <div className={cn('flex gap-2', className)}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            inputs.current[index] = element;
          }}
          id={index === 0 ? baseId : `${baseId}-${index}`}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Chiffre ${index + 1} sur ${length}`}
          aria-invalid={invalid || undefined}
          maxLength={length}
          value={value[index] ?? ''}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.currentTarget.select()}
          className={cn(
            'h-[53px] min-w-0 flex-1 rounded-field border bg-surface text-center text-xl font-semibold text-content transition-colors',
            'border-border-input/50 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/15',
            invalid && 'border-danger focus:border-danger focus:ring-danger/15',
            disabled && 'cursor-not-allowed bg-surface-muted opacity-60',
          )}
          {...props}
        />
      ))}
    </div>
  );
}
